/**
 * DK63 Machine（SDCX）：UI 虚拟布局 ↔ 432B 协议矩阵（108×4B）
 * 布局来源：公版驱动 CMD 0x08/0x07 出厂矩阵抓包 + UI↔矩阵索引反推（非星闪 keyboardType "63"）
 */

import {
  isSdcxMatrixCellOccupied,
  parseSdcxMatrixCell,
  SDCX_CONSUMER_TYPE,
  SDCX_DISABLED_TYPE,
  SDCX_FN_UI_KEYCODE,
  SDCX_FUNC_TYPE,
  SDCX_KB_TYPE,
  SDCX_MACRO_TYPE,
  SDCX_PROFILE_TYPE,
  sdcxMatrixCellToUiKeyCode,
  uiKeyCodeToSdcxMatrixCell,
  writeSdcxMatrixCell,
  type SdcxMatrixCell,
} from "./sdcxKeyEncode";

export const SDCX_DK63_MATRIX_COLS = 14;
export const SDCX_DK63_MATRIX_ROWS = 6;
export const SDCX_DK63_MATRIX_SIZE = 432;
export const SDCX_DK63_MATRIX_LAYER_SIZE = 432;
export const SDCX_DK63_KEY_COUNT = 108;

/** UI [row,col] → 协议矩阵键索引（row 0 为占位行，无映射） */
const UI_TO_MATRIX_INDEX = new Map<string, number>(
  [
    [1, 0, 0],
    [1, 1, 1],
    [1, 2, 2],
    [1, 3, 3],
    [1, 4, 4],
    [1, 5, 5],
    [1, 6, 6],
    [1, 7, 7],
    [1, 8, 8],
    [1, 9, 9],
    [1, 10, 10],
    [1, 11, 11],
    [1, 12, 12],
    [1, 13, 13],
    [2, 0, 15],
    [2, 1, 16],
    [2, 2, 17],
    [2, 3, 18],
    [2, 4, 19],
    [2, 5, 20],
    [2, 6, 21],
    [2, 7, 22],
    [2, 8, 23],
    [2, 9, 24],
    [2, 10, 25],
    [2, 11, 26],
    [2, 12, 27],
    [2, 13, 28],
    [3, 0, 31],
    [3, 1, 32],
    [3, 2, 33],
    [3, 3, 34],
    [3, 4, 35],
    [3, 5, 36],
    [3, 6, 37],
    [3, 7, 38],
    [3, 8, 39],
    [3, 9, 40],
    [3, 10, 41],
    [3, 11, 42],
    [3, 12, 44],
    [4, 1, 49],
    [4, 2, 50],
    [4, 3, 51],
    [4, 4, 52],
    [4, 5, 53],
    [4, 6, 54],
    [4, 7, 55],
    [4, 8, 56],
    [4, 9, 57],
    [4, 10, 58],
    [4, 11, 59],
    [4, 12, 60],
    [5, 3, 61],
    [5, 9, 63],
    [5, 10, 64],
    [5, 11, 65],
    [5, 12, 66],
  ].map(([row, col, index]) => [`${row},${col}`, index as number])
);

/** 各行 UI 列数（row0 占位；与公版 63 键紧凑配列物理行一致） */
const DK63_MACHINE_UI_ROW_WIDTHS = [1, 14, 14, 13, 13, 14] as const;

/**
 * 矩阵无映射格子的 UI 出厂默认（抓包中未出现在 432B 主矩阵区的修饰键 / Fn）
 * 实机读矩阵成功时会被设备值覆盖
 */
const DK63_MACHINE_UNMAPPED_UI_DEFAULTS: Readonly<Record<string, number>> = {
  "4,0": 225,
  "5,0": 224,
  "5,1": 227,
  "5,2": 226,
  "5,13": SDCX_FN_UI_KEYCODE,
};

/** UI HID → 协议热键 Code1（Type=0x20 且 Code2=0） */
const SDCX_DK63_UI_TO_HOTKEY_CODE1: Readonly<Record<number, number>> = {
  224: 0x01,
  225: 0x02,
  226: 0x04,
  227: 0x08,
};

/**
 * 公版实机线位：UI 修饰格 → 矩阵 index
 * L-Ctrl @ 47 / ADDR 0xc0 已由 Bus Hound 实锤；其余为同簇未映射 index，待抓包补全
 */
export const SDCX_DK63_MODIFIER_UI_STATIC_INDEX: Readonly<
  Record<string, number>
> = {
  "5,0": 47,
  "5,1": 48,
  "5,2": 45,
  "4,0": 43,
};

/** 0x10 后须 0x08(矩阵分包) + 0x01×3 方落物理键的线位（Bus Hound 实锤） */
export const SDCX_DK63_SINGLE_KEY_APPLY_RITUAL_KEY_INDICES = new Set<number>([
  ...Object.values(SDCX_DK63_MODIFIER_UI_STATIC_INDEX),
  13,
]);

export function isSdcxDk63SingleKeyApplyRitualRequired(
  keyIndex: number
): boolean {
  return SDCX_DK63_SINGLE_KEY_APPLY_RITUAL_KEY_INDICES.has(keyIndex);
}

/** 出厂矩阵占位列索引（分包对齐指纹） */
export const SDCX_DK63_MATRIX_PLACEHOLDER_INDICES = [
  14, 30, 43, 46, 62,
] as const;
/** 空格 UI 映射格误填为导航/方向键时的 UI 键码 */
const SDCX_DK63_INVALID_SPACE_UI_KEYCODES = new Set([
  75, 76, 77, 78, 79, 80, 81, 82,
]);
/** UI Tab 键码（用于检测分包错位导致的双 Tab 伪影） */
const SDCX_DK63_TAB_UI_KEYCODE = 43;
const SDCX_DK63_LEFT_ARROW_HID = 0x50;
const SDCX_DK63_DOWN_ARROW_HID = 0x51;
/** UI 方向键键码 */
const SDCX_DK63_ARROW_UI_KEYCODES = new Set([80, 81, 79]);
/** UI R-Alt 键码 */
const SDCX_DK63_RALT_UI_KEYCODE = 230;
/** 底行方向位误读为左侧修饰键时的 UI 键码组合 */
const SDCX_DK63_BOTTOM_MODIFIER_UI_KEYCODES = [224, 227, 226] as const;

/**
 * 扫描矩阵中「Space + 占位列 + RAlt/方向键」底行锚点（index61 固定映射失败时使用）
 * 结构：space(i) | gap(i+1) | ralt(i+2) | ←(i+3) | ↓(i+4) | →(i+5)
 */
export function findSdcxDk63SpaceAnchorIndex(
  matrix: Uint8Array
): number | null {
  if (!matrix?.length) return null;
  for (let i = 50; i <= 68; i++) {
    const gap = parseSdcxMatrixCell(matrix, i + 1);
    if (gap.type !== SDCX_DISABLED_TYPE) continue;
    const space = parseSdcxMatrixCell(matrix, i);
    if (space.type !== SDCX_KB_TYPE || !space.code2 || space.code2 === 0x4e) {
      continue;
    }
    const left = parseSdcxMatrixCell(matrix, i + 3);
    const down = parseSdcxMatrixCell(matrix, i + 4);
    if (
      left.type === SDCX_KB_TYPE &&
      down.type === SDCX_KB_TYPE &&
      (left.code2 === SDCX_DK63_LEFT_ARROW_HID ||
        left.code2 === SDCX_DK63_DOWN_ARROW_HID)
    ) {
      return i;
    }
  }
  return null;
}

/** 底行 UI 列 → 矩阵 index（默认 61/63~66，锚点偏移时跟随底行窗口） */
export function resolveSdcxDk63BottomRowMatrixIndices(
  matrix: Uint8Array
): Readonly<Record<number, number>> {
  const anchor = findSdcxDk63SpaceAnchorIndex(matrix) ?? 61;
  return {
    3: anchor,
    9: anchor + 2,
    10: anchor + 3,
    11: anchor + 4,
    12: anchor + 5,
  };
}

/** 解析底行 UI 键码（含空格/方向键锚点映射） */
function buildSdcxDk63Row5Keycodes(matrix: Uint8Array, layer = 0): number[] {
  const width = getSdcxDk63UiRowWidth(5);
  const row = Array.from({ length: width }, (_, col) =>
    resolveSdcxDk63UnmappedUiDefault(5, col, layer)
  );
  const bottomMap = resolveSdcxDk63BottomRowMatrixIndices(matrix);
  for (const [colText, keyIndex] of Object.entries(bottomMap)) {
    const col = Number(colText);
    if (keyIndex < 0 || keyIndex >= SDCX_DK63_KEY_COUNT) continue;
    row[col] = resolveSdcxDk63UiKeyCodeFromMatrix(
      matrix,
      5,
      col,
      keyIndex,
      layer
    );
  }
  return row;
}

/** 是否为 Fn 改键层（layer 1+） */
export function isSdcxDk63FnMatrixLayer(layer = 0): boolean {
  return Math.trunc(layer) >= 1;
}

/** 底行是否解析可信（拒绝 PgDn 占位、方向键误读为 L-Ctrl/Win/L-Alt） */
export function isSdcxDk63MatrixBottomRowDecodable(
  matrix: Uint8Array,
  layer = 0
): boolean {
  return isSdcxDk63MatrixBottomRowDecodableForLayer(matrix, layer);
}

/** 按层校验底行（Fn 层不要求方向键簇，允许 Space 位为功能键） */
export function isSdcxDk63MatrixBottomRowDecodableForLayer(
  matrix: Uint8Array,
  layer = 0
): boolean {
  const row5 = buildSdcxDk63Row5Keycodes(matrix, layer);
  const spaceCode = row5[3] ?? 0;
  if (spaceCode <= 0 || SDCX_DK63_INVALID_SPACE_UI_KEYCODES.has(spaceCode)) {
    return false;
  }
  if (
    row5[9] === SDCX_DK63_BOTTOM_MODIFIER_UI_KEYCODES[0] &&
    row5[10] === SDCX_DK63_BOTTOM_MODIFIER_UI_KEYCODES[1] &&
    row5[11] === SDCX_DK63_BOTTOM_MODIFIER_UI_KEYCODES[2]
  ) {
    return false;
  }
  if (isSdcxDk63FnMatrixLayer(layer)) {
    return (row5[10] ?? 0) > 0 && (row5[11] ?? 0) > 0 && (row5[12] ?? 0) > 0;
  }
  let arrowLike = 0;
  for (const col of [9, 10, 11, 12]) {
    const code = row5[col] ?? 0;
    if (
      code === SDCX_DK63_RALT_UI_KEYCODE ||
      SDCX_DK63_ARROW_UI_KEYCODES.has(code)
    ) {
      arrowLike++;
    }
  }
  return arrowLike >= 3;
}

/** 从矩阵格解析 UI 键码（直接读映射 index，不做单键错位回退） */
function resolveSdcxDk63UiKeyCodeFromMatrix(
  matrix: Uint8Array,
  row: number,
  col: number,
  keyIndex: number,
  layer = 0
): number {
  const cell = parseSdcxMatrixCell(matrix, keyIndex);
  const uiDefault = resolveSdcxDk63UnmappedUiDefault(row, col, layer);
  const modifierKey = `${row},${col}`;
  const isModifierWiring =
    !isSdcxDk63FnMatrixLayer(layer) &&
    Object.prototype.hasOwnProperty.call(
      SDCX_DK63_MODIFIER_UI_STATIC_INDEX,
      modifierKey
    ) &&
    resolveSdcxDk63ModifierUiMatrixIndex(row, col, matrix) === keyIndex;

  if (cell.type !== SDCX_DISABLED_TYPE && isSdcxMatrixCellOccupied(cell)) {
    const fromMatrix = sdcxMatrixCellToUiKeyCode(cell, keyIndex);
    if (isModifierWiring) {
      /**
       * 修饰键线位：出厂格多为普通 HID（如 index47=\），固件读回 code1 热键不可信。
       * 仅当 code2 相对出厂发生变化时视为用户改键（公版 0x10 写普通键）。
       */
      const factoryCell = parseSdcxMatrixCell(
        SDCX_DK63_FACTORY_MATRIX_BYTES,
        keyIndex
      );
      if (
        cell.type === SDCX_KB_TYPE &&
        cell.code2 &&
        cell.code2 !== factoryCell.code2 &&
        fromMatrix
      ) {
        return fromMatrix;
      }
      return uiDefault;
    }
    if (cell.type === SDCX_KB_TYPE && cell.code1 && !cell.code2) {
      const hotkeyUi = sdcxMatrixCellToUiKeyCode(cell, keyIndex);
      if (hotkeyUi) return hotkeyUi;
    }
    if (fromMatrix) {
      return fromMatrix;
    }
  }
  return uiDefault;
}

function parseHexMatrixChunks(chunks: string[]): Uint8Array {
  const matrix = new Uint8Array(SDCX_DK63_MATRIX_SIZE);
  let offset = 0;
  for (const chunk of chunks) {
    const bytes = chunk
      .trim()
      .split(/\s+/)
      .map((hex) => parseInt(hex, 16));
    matrix.set(bytes, offset);
    offset += bytes.length;
  }
  return matrix;
}

/** 公版驱动抓包出厂默认矩阵（CMD 0x08/0x07 首层） */
export const SDCX_DK63_FACTORY_MATRIX_BYTES = parseHexMatrixChunks([
  "20 00 29 00 20 00 1e 00 20 00 1f 00 20 00 20 00 20 00 21 00 20 00 22 00 20 00 23 00 20 00 24 00 20 00 25 00 20 00 26 00 20 00 27 00 20 00 2d 00 20 00 2e 00",
  "20 00 35 00 13 00 00 00 20 00 2b 00 20 00 14 00 20 00 1a 00 20 00 08 00 20 00 15 00 20 00 17 00 20 00 1c 00 20 00 18 00 20 00 0c 00 20 00 12 00 20 00 13 00 20 00 2f 00",
  "20 00 30 00 20 00 31 00 20 00 4c 00 13 00 00 00 20 00 39 00 20 00 04 00 20 00 16 00 20 00 07 00 20 00 09 00 20 00 0a 00 20 00 0b 00 20 00 0d 00 20 00 0e 00 20 00 0f 00",
  "20 00 33 00 20 00 34 00 13 00 00 00 20 00 28 00 20 00 4b 00 13 00 00 00 20 00 2a 00 20 00 38 00 20 00 1d 00 20 00 1b 00 20 00 06 00 20 00 19 00 20 00 05 00 20 00 11 00",
  "20 00 10 00 20 00 36 00 20 00 37 00 20 00 38 00 20 00 52 00 20 20 00 00 20 00 2c 00 13 00 00 00 20 40 00 00 20 00 50 00 20 00 51 00 20 00 4f 00",
  "20 00 3d 00 20 00 3e 00 20 00 3f 00 20 00 40 00 20 00 41 00 20 00 42 00 20 00 43 00 20 00 44 00 20 00 45 00 20 00 46 00 20 00 47 00 20 00 48 00 20 00 49 00 20 00 4a 00",
  "20 00 1a 00 20 00 08 00 20 00 15 00 20 00 17 00 20 00 1c 00 20 00 18 00",
  "1f 10 00 00 1f 0e 00 00 1f 0d 00 00 20 00 07 00 20 00 09 00 20 00 0a 00",
]);

/**
 * 公版驱动 Fn 层 UI 键码（标准层物理键帽 + Fn 组合功能）
 * 来源：hub.eweadn.cn SDCX DK63 Fn 层出厂布局
 */
export const LAYOUT_DK63_FN_KEYCODES: number[][] = [
  [0],
  [53, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 62215],
  [43, 20, 26, 8, 21, 23, 28, 24, 70, 71, 72, 47, 48, 62208],
  [61952, 61953, 61954, 7, 9, 10, 11, 73, 74, 75, 51, 52, 40],
  [225, 29, 27, 6, 25, 5, 17, 16, 76, 77, 78, 62210, 229],
  [224, 61961, 226, 62224, 0, 0, 0, 0, 0, 230, 62213, 62211, 62212, 61697],
];

/** 无矩阵映射格的 UI 出厂默认（Fn 层用公版 Fn 布局，默认层用修饰键默认） */
function resolveSdcxDk63UnmappedUiDefault(
  row: number,
  col: number,
  layer = 0
): number {
  if (isSdcxDk63FnMatrixLayer(layer)) {
    return LAYOUT_DK63_FN_KEYCODES[row]?.[col] ?? 0;
  }
  return DK63_MACHINE_UNMAPPED_UI_DEFAULTS[`${row},${col}`] ?? 0;
}

/** 由 UI 布局反写协议矩阵（默认层结构为模板，仅覆盖 UI 映射格） */
export function buildSdcxDk63MatrixFromLayout(
  layout: number[][],
  template: Uint8Array = SDCX_DK63_FACTORY_MATRIX_BYTES
): Uint8Array {
  const matrix = template.slice();
  for (let row = 1; row <= 5; row++) {
    const width = getSdcxDk63UiRowWidth(row);
    for (let col = 0; col < width; col++) {
      const keyIndex = resolveSdcxDk63MatrixIndex(row, col);
      if (keyIndex == null) continue;
      const keyCode = layout[row]?.[col] ?? 0;
      if (!keyCode) continue;
      const cell = uiKeyCodeToSdcxMatrixCell(keyCode);
      if (cell) writeSdcxMatrixCell(matrix, keyIndex, cell);
    }
  }
  return matrix;
}

/** Fn 层出厂矩阵：由公版 Fn UI 布局编码生成 */
export const SDCX_DK63_FN_FACTORY_MATRIX_BYTES = buildSdcxDk63MatrixFromLayout(
  LAYOUT_DK63_FN_KEYCODES
);

/** 由 SDCX 协议矩阵 + UI 映射反推 UI 出厂键码（静态兜底 / 读失败回退） */
export function buildLayoutKeycodesFromSdcxMatrix(
  matrix: Uint8Array,
  layer = 0
): number[][] {
  return DK63_MACHINE_UI_ROW_WIDTHS.map((width, row) => {
    if (row === 0) return [0];
    return Array.from({ length: width }, (_, col) => {
      const keyIndex = resolveSdcxDk63RemapMatrixIndex(row, col, matrix);
      if (keyIndex != null) {
        return resolveSdcxDk63UiKeyCodeFromMatrix(
          matrix,
          row,
          col,
          keyIndex,
          layer
        );
      }
      return resolveSdcxDk63UnmappedUiDefault(row, col, layer);
    });
  });
}

/** 公版驱动出厂矩阵反推的 UI 键码（非星闪 LAYOUT_63 配置拷贝） */
export const LAYOUT_DK63_MACHINE_KEYCODES = buildLayoutKeycodesFromSdcxMatrix(
  SDCX_DK63_FACTORY_MATRIX_BYTES
);

export function resolveSdcxDk63MatrixIndex(
  uiRow: number,
  uiCol: number
): number | null {
  const index = UI_TO_MATRIX_INDEX.get(`${uiRow},${uiCol}`);
  return index == null ? null : index;
}

function findSdcxDk63HotkeyMatrixIndex(
  matrix: Uint8Array,
  hotkeyCode1: number
): number | null {
  if (!matrix?.length || !hotkeyCode1) return null;
  for (let i = 0; i < SDCX_DK63_KEY_COUNT; i++) {
    const cell = parseSdcxMatrixCell(matrix, i);
    if (
      cell.type === SDCX_KB_TYPE &&
      cell.code1 === hotkeyCode1 &&
      !cell.code2
    ) {
      return i;
    }
  }
  return null;
}

/** 左侧修饰键 UI 格 → 矩阵 index（先扫 0x08 热键位，再回退公版线位表） */
export function resolveSdcxDk63ModifierUiMatrixIndex(
  row: number,
  col: number,
  matrix: Uint8Array
): number | null {
  const key = `${row},${col}`;
  const uiKeyCode = DK63_MACHINE_UNMAPPED_UI_DEFAULTS[key];
  if (uiKeyCode == null || uiKeyCode > 255) return null;
  const hotkeyCode1 = SDCX_DK63_UI_TO_HOTKEY_CODE1[uiKeyCode];
  if (hotkeyCode1 != null) {
    const scanned = findSdcxDk63HotkeyMatrixIndex(matrix, hotkeyCode1);
    if (scanned != null) return scanned;
  }
  return SDCX_DK63_MODIFIER_UI_STATIC_INDEX[key] ?? null;
}

/** 改键写表用矩阵 index：修饰键线位 → 底行锚点 → 静态 UI 映射 */
export function resolveSdcxDk63RemapMatrixIndex(
  row: number,
  col: number,
  matrix: Uint8Array
): number | null {
  const modifierIndex = resolveSdcxDk63ModifierUiMatrixIndex(row, col, matrix);
  if (modifierIndex != null) return modifierIndex;
  if (row === 5) {
    const bottomMap = resolveSdcxDk63BottomRowMatrixIndices(matrix);
    if (Object.prototype.hasOwnProperty.call(bottomMap, col)) {
      return bottomMap[col as keyof typeof bottomMap] ?? null;
    }
  }
  return resolveSdcxDk63MatrixIndex(row, col);
}

export function isSdcxDk63RemappableCell(
  row: number,
  col: number,
  matrix: Uint8Array
): boolean {
  return resolveSdcxDk63RemapMatrixIndex(row, col, matrix) != null;
}

/** 公版矩阵紧凑分包各包 copyLen（与 CMD 0x08/0x09 读拼装对称） */
export const SDCX_DK63_MATRIX_PACKET_COPY_LENS = [
  52, 56, 56, 56, 24, 24, 56, 24, 24,
] as const;

/** 矩阵分包 ADDR 步进（0x00 → 0x38 → … → 0x118） */
export const SDCX_DK63_MATRIX_CHUNK_STEP = 0x38;

/**
 * 矩阵 keyIndex → CMD 0x10 单键 ADDR（层内偏移）
 * 公版 Space(index61) 用 0x0118 而非线性 0xf4；按紧凑分包页地址 + 页内偏移计算
 */
export function resolveSdcxDk63SingleKeyWriteAddrInLayer(
  keyIndex: number
): number {
  const linearOffset = Math.max(0, keyIndex) * 4;
  let assembledOffset = 0;
  for (
    let packetIndex = 0;
    packetIndex < SDCX_DK63_MATRIX_PACKET_COPY_LENS.length;
    packetIndex++
  ) {
    const copyLen = SDCX_DK63_MATRIX_PACKET_COPY_LENS[packetIndex]!;
    if (linearOffset < assembledOffset + copyLen) {
      return (
        packetIndex * SDCX_DK63_MATRIX_CHUNK_STEP +
        (linearOffset - assembledOffset)
      );
    }
    assembledOffset += copyLen;
  }
  return linearOffset;
}

/** 矩阵 keyIndex → CMD 0x10 单键 ADDR（含层基址） */
export function resolveSdcxDk63SingleKeyWriteAddr(
  layer: number,
  keyIndex: number
): number {
  return (
    Math.max(0, layer) * SDCX_DK63_MATRIX_LAYER_SIZE +
    resolveSdcxDk63SingleKeyWriteAddrInLayer(keyIndex)
  );
}

/** keyIndex 所在矩阵分包的起始 ADDR（层内，如 Space index61 → 0x118） */
export function resolveSdcxDk63MatrixPacketBaseAddrInLayer(
  keyIndex: number
): number {
  const linearOffset = Math.max(0, keyIndex) * 4;
  let assembledOffset = 0;
  for (
    let packetIndex = 0;
    packetIndex < SDCX_DK63_MATRIX_PACKET_COPY_LENS.length;
    packetIndex++
  ) {
    const copyLen = SDCX_DK63_MATRIX_PACKET_COPY_LENS[packetIndex]!;
    if (linearOffset < assembledOffset + copyLen) {
      return packetIndex * SDCX_DK63_MATRIX_CHUNK_STEP;
    }
    assembledOffset += copyLen;
  }
  return 0;
}

/** keyIndex 在所在分包 DATA 区内的字节偏移 */
export function resolveSdcxDk63KeyOffsetInMatrixPacket(
  keyIndex: number
): number {
  return (
    resolveSdcxDk63SingleKeyWriteAddrInLayer(keyIndex) -
    resolveSdcxDk63MatrixPacketBaseAddrInLayer(keyIndex)
  );
}

export function isSdcxDk63PhysicalCell(row: number, col: number): boolean {
  return resolveSdcxDk63MatrixIndex(row, col) != null;
}

export function isSdcxDk63DisplayCell(
  row: number,
  col: number,
  cell: SdcxMatrixCell
): boolean {
  if (!isSdcxDk63PhysicalCell(row, col)) return false;
  /** 矩阵占位列（0x13）回退静态布局，避免 Enter / \\ 等键丢失 */
  if (cell.type === SDCX_DISABLED_TYPE) return false;
  return isSdcxMatrixCellOccupied(cell);
}

/** 读回的矩阵是否包含足够有效键位 */
export function isSdcxDk63MatrixUsable(matrix: Uint8Array): boolean {
  if (!matrix?.length || matrix.length < SDCX_DK63_MATRIX_SIZE) {
    return false;
  }
  let occupied = 0;
  for (let i = 0; i < SDCX_DK63_KEY_COUNT; i++) {
    if (isSdcxMatrixCellOccupied(parseSdcxMatrixCell(matrix, i))) {
      occupied++;
    }
  }
  return occupied >= 50;
}

const SDCX_VALID_MATRIX_CELL_TYPES = new Set<number>([
  SDCX_KB_TYPE,
  SDCX_DISABLED_TYPE,
  SDCX_FUNC_TYPE,
  SDCX_CONSUMER_TYPE,
  SDCX_MACRO_TYPE,
  SDCX_PROFILE_TYPE,
]);

/** 矩阵单元类型是否合法（用于区分分包错位垃圾数据，与用户改键无关） */
function isSdcxMatrixCellTypeValid(type: number): boolean {
  return SDCX_VALID_MATRIX_CELL_TYPES.has(type);
}

/** index0 是否为有效首键（允许 Esc 改键，不限定 0x29） */
export function isSdcxDk63MatrixLeadCellPresent(matrix: Uint8Array): boolean {
  if (!matrix?.length) return false;
  const leadCell = parseSdcxMatrixCell(matrix, 0);
  return leadCell.type === SDCX_KB_TYPE && leadCell.code2 !== 0;
}

/** trim 后典型伪首键：整表前移 4B 时 index0 会变成数字 1（0x1e） */
export function isSdcxDk63MatrixTrimArtifactLead(matrix: Uint8Array): boolean {
  const leadCell = parseSdcxMatrixCell(matrix, 0);
  return leadCell.type === SDCX_KB_TYPE && leadCell.code2 === 0x1e;
}

/** 矩阵单元边界是否对齐（结构占位列 + 首键/空格锚点，Esc 可改键） */
export function isSdcxDk63MatrixAligned(
  matrix: Uint8Array,
  layer = 0
): boolean {
  return isSdcxDk63MatrixAlignedForLayer(matrix, layer);
}

/** 按层校验矩阵结构锚点（Fn 层允许 index13/index61 为 0x1f 功能键） */
export function isSdcxDk63MatrixAlignedForLayer(
  matrix: Uint8Array,
  layer = 0
): boolean {
  if (!matrix?.length || matrix.length < SDCX_DK63_MATRIX_SIZE) {
    return false;
  }
  if (isSdcxDk63MatrixTrimArtifactLead(matrix)) {
    return false;
  }
  if (!isSdcxDk63MatrixLeadCellPresent(matrix)) {
    return false;
  }
  const rowGap = parseSdcxMatrixCell(matrix, 14);
  if (rowGap.type !== SDCX_DISABLED_TYPE) {
    return false;
  }
  const bottomGap = parseSdcxMatrixCell(matrix, 62);
  if (bottomGap.type !== SDCX_DISABLED_TYPE) {
    return false;
  }

  if (isSdcxDk63FnMatrixLayer(layer)) {
    const spaceCell = parseSdcxMatrixCell(matrix, 61);
    if (!isSdcxMatrixCellOccupied(spaceCell)) {
      return false;
    }
    if (
      spaceCell.type === SDCX_KB_TYPE &&
      (spaceCell.code2 === 0x4e || !spaceCell.code2)
    ) {
      return false;
    }
    const row1TailCell = parseSdcxMatrixCell(matrix, 13);
    return isSdcxMatrixCellOccupied(row1TailCell);
  }

  /** 默认层：底行 Space 锚点（分包错位时滑动窗口搜索） */
  const spaceAnchor = findSdcxDk63SpaceAnchorIndex(matrix) ?? 61;
  const fnBottomGap = parseSdcxMatrixCell(matrix, spaceAnchor + 1);
  if (fnBottomGap.type !== SDCX_DISABLED_TYPE) {
    return false;
  }
  const spaceCell = parseSdcxMatrixCell(matrix, spaceAnchor);
  if (spaceCell.type !== SDCX_KB_TYPE || !spaceCell.code2) {
    return false;
  }
  if (spaceCell.code2 === 0x4e) {
    return false;
  }
  const row1TailCell = parseSdcxMatrixCell(matrix, 13);
  if (row1TailCell.type !== SDCX_KB_TYPE || !row1TailCell.code2) {
    return false;
  }
  return true;
}

/** 分包错位时第 2 行前两格会同时解析为 Tab */
export function isSdcxDk63MatrixRow2DoubleTabArtifact(
  matrix: Uint8Array,
  layer = 0
): boolean {
  const layout = buildLayoutKeycodesFromSdcxMatrix(matrix, layer);
  const row2 = layout[2] ?? [];
  return (
    row2[0] === SDCX_DK63_TAB_UI_KEYCODE && row2[1] === SDCX_DK63_TAB_UI_KEYCODE
  );
}

/** 矩阵解析后 UI 是否结构可信（只验锚点/行完整性，不绑定出厂键值） */
export function isSdcxDk63MatrixLayoutDecodable(
  matrix: Uint8Array,
  layer = 0
): boolean {
  return isSdcxDk63MatrixLayoutDecodableForLayer(matrix, layer);
}

/** 按层判断矩阵是否可解码为 UI 布局 */
export function isSdcxDk63MatrixLayoutDecodableForLayer(
  matrix: Uint8Array,
  layer = 0
): boolean {
  if (!isSdcxDk63MatrixAlignedForLayer(matrix, layer)) {
    return false;
  }
  if (isSdcxDk63MatrixRow2DoubleTabArtifact(matrix, layer)) {
    return false;
  }
  const layout = buildLayoutKeycodesFromSdcxMatrix(matrix, layer);
  const row1 = layout[1] ?? [];
  const row2 = layout[2] ?? [];
  if (!(row1[0] > 0) || !(row1[13] > 0)) {
    return false;
  }
  if (!(row2[0] > 0) || row2.filter((code) => code > 0).length < 12) {
    return false;
  }
  if (isSdcxDk63FnMatrixLayer(layer)) {
    const capsCell = parseSdcxMatrixCell(matrix, 31);
    /** 误读默认层时 Caps 仍为 0x39，须拒绝 */
    if (capsCell.type === SDCX_KB_TYPE && capsCell.code2 === 0x39) {
      return false;
    }
  }
  return isSdcxDk63MatrixBottomRowDecodableForLayer(matrix, layer);
}

/** 占位列命中数（用于分包拼装择优） */
export function scoreSdcxDk63MatrixPlaceholderMatch(
  matrix: Uint8Array
): number {
  let score = 0;
  for (const index of SDCX_DK63_MATRIX_PLACEHOLDER_INDICES) {
    if (parseSdcxMatrixCell(matrix, index).type === SDCX_DISABLED_TYPE) {
      score += 12;
    }
  }
  return score;
}

/** 分包拼装结构锚点评分（择优 boundary / 拒绝 trim 误选） */
export function scoreSdcxDk63MatrixAssemblyAnchors(
  matrix: Uint8Array,
  layer = 0
): number {
  if (!matrix?.length || matrix.length < SDCX_DK63_MATRIX_SIZE) {
    return -100;
  }
  let score = 0;
  if (isSdcxDk63MatrixLeadCellPresent(matrix)) {
    score += 35;
  } else {
    score -= 60;
  }
  if (isSdcxDk63MatrixTrimArtifactLead(matrix)) {
    score -= 80;
  }
  const rowGap = parseSdcxMatrixCell(matrix, 14);
  if (rowGap.type === SDCX_DISABLED_TYPE) {
    score += 35;
  } else {
    score -= 45;
  }
  const bottomGap = parseSdcxMatrixCell(matrix, 62);
  if (bottomGap.type === SDCX_DISABLED_TYPE) {
    score += 25;
  } else {
    score -= 25;
  }
  const row1TailCell = parseSdcxMatrixCell(matrix, 13);
  if (isSdcxDk63FnMatrixLayer(layer)) {
    if (row1TailCell.type === SDCX_FUNC_TYPE && row1TailCell.code1) {
      score += 30;
    }
  } else if (row1TailCell.type === SDCX_KB_TYPE && row1TailCell.code2) {
    score += 30;
  }
  if (isSdcxDk63MatrixRow2DoubleTabArtifact(matrix, layer)) {
    score -= 120;
  }
  if (!isSdcxDk63MatrixBottomRowDecodableForLayer(matrix, layer)) {
    score -= 150;
  }
  const spaceCell = parseSdcxMatrixCell(matrix, 61);
  if (isSdcxDk63FnMatrixLayer(layer)) {
    if (spaceCell.type === SDCX_FUNC_TYPE && spaceCell.code1) {
      score += 30;
    } else if (spaceCell.type === SDCX_KB_TYPE && spaceCell.code2 === 0x4e) {
      score -= 50;
    }
    const capsCell = parseSdcxMatrixCell(matrix, 31);
    if (capsCell.type === SDCX_FUNC_TYPE && capsCell.code1 === 0x05) {
      score += 80;
    }
    const winDevCell = parseSdcxMatrixCell(matrix, 32);
    const macDevCell = parseSdcxMatrixCell(matrix, 33);
    if (winDevCell.type === SDCX_FUNC_TYPE && winDevCell.code1 === 0x0d) {
      score += 40;
    }
    if (macDevCell.type === SDCX_FUNC_TYPE && macDevCell.code1 === 0x0e) {
      score += 40;
    }
  } else if (spaceCell.type === SDCX_KB_TYPE && spaceCell.code2 === 0x2c) {
    score += 30;
  } else if (spaceCell.type === SDCX_KB_TYPE && spaceCell.code2 === 0x4e) {
    score -= 50;
  } else if (spaceCell.type === SDCX_KB_TYPE && spaceCell.code2) {
    score += 8;
  } else if (
    spaceCell.type === SDCX_KB_TYPE &&
    spaceCell.code1 &&
    !spaceCell.code2
  ) {
    score -= 35;
  }
  score += scoreSdcxDk63MatrixPlaceholderMatch(matrix);
  if (isSdcxDk63MatrixLayoutDecodableForLayer(matrix, layer)) {
    score += 100;
  }
  return score;
}

/** 读回矩阵结构是否可信（允许与出厂不同，拒绝明显错位的分包数据） */
export function isSdcxDk63MatrixStructurallyValid(matrix: Uint8Array): boolean {
  if (!isSdcxDk63MatrixUsable(matrix)) {
    return false;
  }
  let mapped = 0;
  let validType = 0;
  let decodable = 0;
  for (let row = 1; row <= 5; row++) {
    const width = getSdcxDk63UiRowWidth(row);
    for (let col = 0; col < width; col++) {
      const keyIndex = resolveSdcxDk63MatrixIndex(row, col);
      if (keyIndex == null) continue;
      mapped++;
      const cell = parseSdcxMatrixCell(matrix, keyIndex);
      if (isSdcxMatrixCellTypeValid(cell.type)) {
        validType++;
      }
      if (cell.type === SDCX_DISABLED_TYPE) {
        decodable++;
        continue;
      }
      if (sdcxMatrixCellToUiKeyCode(cell, keyIndex) > 0) {
        decodable++;
      }
    }
  }
  if (mapped === 0) return false;
  return validType / mapped >= 0.92 && decodable / mapped >= 0.72;
}

/** 矩阵读包质量评分（多分包参数择优；与用户改键无关） */
export function scoreSdcxDk63MatrixRead(matrix: Uint8Array, layer = 0): number {
  if (!matrix?.length || matrix.length < SDCX_DK63_MATRIX_SIZE) {
    return -1;
  }
  let score = 0;
  for (let i = 0; i < SDCX_DK63_KEY_COUNT; i++) {
    const cell = parseSdcxMatrixCell(matrix, i);
    if (cell.type === 0) continue;
    if (isSdcxMatrixCellTypeValid(cell.type)) {
      score += 2;
      if (cell.type === SDCX_KB_TYPE && (cell.code1 || cell.code2)) {
        score += 1;
      }
      if (isSdcxDk63FnMatrixLayer(layer) && cell.type === SDCX_FUNC_TYPE) {
        score += 1;
      }
    } else {
      score -= 4;
    }
  }
  if (isSdcxDk63MatrixStructurallyValid(matrix)) {
    score += 120;
  }
  if (isSdcxDk63MatrixAlignedForLayer(matrix, layer)) {
    score += 200;
  }
  score += scoreSdcxDk63MatrixAssemblyAnchors(matrix, layer);
  if (
    isSdcxDk63FnMatrixLayer(layer) &&
    isSdcxDk63FnMatrixMatchingFactory(matrix)
  ) {
    score += 120;
  }
  return score;
}

/** 读回矩阵与出厂快照在 UI 映射格上是否一致（仅用于调试/分包择优，不可用于拒绝用户改键） */
export function isSdcxDk63MatrixMatchingFactory(
  matrix: Uint8Array,
  minMatchRatio = 0.85
): boolean {
  if (!matrix?.length || matrix.length < SDCX_DK63_MATRIX_SIZE) {
    return false;
  }
  let mapped = 0;
  let matched = 0;
  for (let row = 1; row <= 5; row++) {
    const width = getSdcxDk63UiRowWidth(row);
    for (let col = 0; col < width; col++) {
      const keyIndex = resolveSdcxDk63MatrixIndex(row, col);
      if (keyIndex == null) continue;
      mapped++;
      const live = parseSdcxMatrixCell(matrix, keyIndex);
      const factory = parseSdcxMatrixCell(
        SDCX_DK63_FACTORY_MATRIX_BYTES,
        keyIndex
      );
      if (
        live.type === factory.type &&
        live.code1 === factory.code1 &&
        live.code2 === factory.code2 &&
        live.code3 === factory.code3
      ) {
        matched++;
      }
    }
  }
  return mapped > 0 && matched / mapped >= minMatchRatio;
}

/** 读回 Fn 层矩阵与出厂快照在 UI 映射格上是否一致（仅调试/择优） */
export function isSdcxDk63FnMatrixMatchingFactory(
  matrix: Uint8Array,
  minMatchRatio = 0.85
): boolean {
  if (!matrix?.length || matrix.length < SDCX_DK63_MATRIX_SIZE) {
    return false;
  }
  let mapped = 0;
  let matched = 0;
  for (let row = 1; row <= 5; row++) {
    const width = getSdcxDk63UiRowWidth(row);
    for (let col = 0; col < width; col++) {
      const keyIndex = resolveSdcxDk63MatrixIndex(row, col);
      if (keyIndex == null) continue;
      mapped++;
      const live = parseSdcxMatrixCell(matrix, keyIndex);
      const factory = parseSdcxMatrixCell(
        SDCX_DK63_FN_FACTORY_MATRIX_BYTES,
        keyIndex
      );
      if (
        live.type === factory.type &&
        live.code1 === factory.code1 &&
        live.code2 === factory.code2 &&
        live.code3 === factory.code3
      ) {
        matched++;
      }
    }
  }
  return mapped > 0 && matched / mapped >= minMatchRatio;
}

/** 按 UI 行取 dense 列数 */
export function getSdcxDk63UiRowWidth(row: number): number {
  return (
    DK63_MACHINE_UI_ROW_WIDTHS[row] ??
    LAYOUT_DK63_MACHINE_KEYCODES[row]?.length ??
    SDCX_DK63_MATRIX_COLS
  );
}
